import type { WorkspaceApp } from 'src/api/facade/types'
import { buildOrganizationUrl } from 'src/utils/postLoginRedirect'

import { THIRD_PARTY_CUSTOM_APP_TYPE } from '../constants/constants'

/**
 * Launch URL for an app's "Open app" action, or `null` when there is nothing
 * safe to open.
 *
 * The rule is driven by app type, because the two kinds have different
 * authoritative sources:
 *
 * - **Third-party custom apps** live outside the platform, so the only
 *   trustworthy target is the `appUrl` the admin typed. When it is missing we
 *   return `null` rather than guessing — a synthesised launcher would point at a
 *   route the external app does not serve.
 *
 * - **Built-in apps** are served by the platform, so the target is the launcher
 *   on the workspace's own origin:
 *   `{workspaceOrigin}/apps/{instance_type}/{app_token}`
 *   e.g. `https://skalx.skalx.dev/apps/crm/2bf6629a…`. The origin comes from the
 *   workspace `domain_name` + `VITE_APP_DOMAIN_URL`, the same composition the
 *   topbar's "open workspace" link uses — never the account app's own host.
 *
 * `app.domain` is deliberately NOT a fallback: it is the API / redirect / CORS
 * host (e.g. `api.crm.skalx.dev`), not a page a user can open.
 */
export const buildAppLaunchUrl = (
  app: WorkspaceApp,
  domainName?: string | null
): string | null => {
  const appUrl = app.appUrl?.trim()

  if (app.instanceTypeCode === THIRD_PARTY_CUSTOM_APP_TYPE) {
    return appUrl || null
  }

  // An explicit appUrl still wins for a platform app — an admin who set one
  // meant it.
  if (appUrl) {
    return appUrl
  }

  if (!app.token || !app.instanceTypeCode || !domainName) {
    return null
  }

  const origin = buildOrganizationUrl(domainName)

  return origin ? `${origin}/apps/${app.instanceTypeCode}/${app.token}` : null
}
